/**
 * autosave — the debounced, single-flight, optimistic-lock save engine (build-plan §5.4, D6).
 *
 * It is the ONE place the builder talks to `PUT /api/builder/page` for drafts. The store's `commit()`
 * choke-point calls `scheduleAutosave()` after every mutation; this module decides WHEN and HOW the
 * document actually reaches the server, and reconciles the `@Version` token without a re-fetch.
 *
 * Design (mapped to the plan §5.4 / §5.5):
 *  - Debounce ~800ms with a 5s max-wait, so a continuous typing burst still flushes periodically.
 *  - SINGLE-FLIGHT: never two PUTs in flight. An edit arriving mid-save sets a "follow-up" flag; when
 *    the in-flight save lands, exactly one more save is scheduled (queue-of-one, never a pile-up).
 *  - VERSION SWAP, no re-fetch: a 200 returns the new `@Version`; we swap it in and keep editing. The
 *    document is still marked dirty only if it changed WHILE the PUT was in flight (compared by a
 *    serialized marker), so a mid-flight edit isn't silently dropped.
 *  - 401 is handled one layer down by `http.ts` (single-flight refresh + one replay) — we never see it.
 *  - 409 → non-destructive 'conflict' state (the reconcile UI takes over; autosave parks itself).
 *  - Network error → 'offline', exponential backoff (2→4→8→…30s), resume on `window 'online'`. The
 *    pending document is NOT lost (it lives in the store).
 *  - Force-flush triggers (manual Save, Publish, route-leave) call `flushSave()` directly.
 *  - tab-hide / blur last-save uses a `fetch(keepalive:true)` that carries the Bearer (NOT sendBeacon,
 *    which can't attach the Authorization header → silent 401) — `keepaliveFlush()`.
 *
 * Kept deliberately framework-free (plain functions over the store) so it is unit-testable with fake
 * timers and a mocked api/http layer, and so StrictMode's double-effect can't double-schedule it.
 */
import { getToken } from '../boot/session';
import { isReadOnlyView } from '../boot/view-handoff';
import { deriveApiBaseUrl } from '../boot/config';
import { builderApi, type BuilderRoute } from '../lib/builderApi';
import {
  ConflictError,
  HttpError,
  NoStoreError,
  SessionExpiredError,
  isNetworkError,
  isStoreChangedError,
} from '../lib/errors';
import type { SaveFailure } from './builderStore';
import {
  isThemeWorkspaceHandle,
  serialize,
  serializeChrome,
  serializeComposed,
  useBuilderStore,
} from './builderStore';
import {
  adoptChromeSavedVersion,
  adoptEditedChrome,
  keepaliveChromePayloads,
  publishChromeDrafts,
  reconcileChromeVersions,
  saveChromeDrafts,
} from './globalChrome';

export const DEBOUNCE_MS = 800;
export const MAX_WAIT_MS = 5000;
const BACKOFF_MIN_MS = 2000;
const BACKOFF_MAX_MS = 30_000;

// NB: reference `useBuilderStore` through this getter, NEVER capture it into a module-eval `const`.
// builderStore.ts ↔ autosave.ts is a circular import (the store's commit() calls scheduleAutosave);
// a `const store = useBuilderStore` at module-eval time would bind to `undefined` because this module
// finishes evaluating before builderStore exports the hook. A function reads the (now-resolved) live
// binding at CALL time, which is always after both modules have evaluated. (build-plan §5.2 / §5.4)
function store() {
  return useBuilderStore;
}

// ── debounce-with-max-wait timer state (module-scoped singletons) ─────────────
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
let backoffTimer: ReturnType<typeof setTimeout> | null = null;
let backoffMs = BACKOFF_MIN_MS;

/** A second edit arrived while a PUT was in flight → schedule exactly one more save when it lands. */
let pendingFollowUp = false;
/**
 * How long the keepalive PUT may hold the single-flight gate before we give the editor its autosave
 * back. The response normally lands in milliseconds (the page is alive — it was only blurred); this is
 * the floor under the case where it never does, so a stalled request can never silently freeze saving.
 */
const KEEPALIVE_RESPONSE_MS = 15_000;
let keepaliveTimer: ReturnType<typeof setTimeout> | null = null;
/** True while `inFlight` is held by a keepalive PUT (so a late release can't cancel an ordinary save). */
let keepaliveHolding = false;
/**
 * The SINGLE-FLIGHT in-flight guard. This is the source of truth, NOT `saveState`: every `commit()`
 * resets `saveState` to 'unsaved' (it can't know a PUT is mid-flight), so reading the store's state to
 * decide "is a save running" would let an edit-during-save start a second overlapping PUT. A dedicated
 * module flag is immune to that reset. (Surfaced bug: edit mid-flight → two overlapping PUTs.)
 */
let inFlight = false;

function clearDebounceTimers(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (maxWaitTimer) {
    clearTimeout(maxWaitTimer);
    maxWaitTimer = null;
  }
}

function clearBackoff(): void {
  if (backoffTimer) {
    clearTimeout(backoffTimer);
    backoffTimer = null;
  }
}

function fire(): void {
  clearDebounceTimers();
  void flushSave('auto');
}

/**
 * Schedule a debounced autosave (build-plan §5.4). Called from the store's `commit()` after every
 * mutation. The debounce resets on each edit, but a max-wait timer guarantees a flush during a
 * continuous burst so the seller never types for 5s with nothing saved. A no-op while parked in a
 * conflict (the reconcile UI owns the next write).
 */
export function scheduleAutosave(): void {
  // Superadmin consultation: never arm a save — the server refuses every write anyway (403
  // READ_ONLY_VIEW); arming would only paint red save states over a working read-only editor.
  if (isReadOnlyView()) return;
  if (store().getState().saveState === 'conflict') return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fire, DEBOUNCE_MS);
  if (!maxWaitTimer) maxWaitTimer = setTimeout(fire, MAX_WAIT_MS);
}

/** Cancel any pending autosave (used when parking in a conflict, or on store teardown/test reset). */
export function cancelAutosave(): void {
  clearDebounceTimers();
  clearBackoff();
  if (keepaliveTimer) {
    clearTimeout(keepaliveTimer);
    keepaliveTimer = null;
  }
  keepaliveHolding = false;
  pendingFollowUp = false;
  inFlight = false;
}

/**
 * The core save. Single-flight, version-swapping, error-classifying. `reason` is informational
 * (auto/manual/publish/route-leave). Resolves when this attempt settles; never throws (errors are
 * reflected into `saveState`).
 */
export async function flushSave(reason: SaveReason = 'auto'): Promise<void> {
  clearDebounceTimers();
  if (isReadOnlyView()) return; // superadmin consultation: nothing may be written
  const s = store().getState();

  // Nothing to do, or the reconcile UI owns the next write.
  if (!s.dirty || s.saveState === 'conflict') return;

  // SINGLE-FLIGHT: an edit during an in-flight save queues exactly one follow-up. Read the dedicated
  // module flag, NOT saveState (which commit() resets to 'unsaved' on every keystroke).
  if (inFlight) {
    pendingFollowUp = true;
    return;
  }

  inFlight = true;
  store().setState({ saveState: 'saving' });
  // Snapshot of what we're about to send, so we can tell whether the doc changed mid-flight.
  const marker = serializedDoc();

  try {
    // The editing surface is the page body PLUS the store's global chrome, persisted as separate rows.
    // `serialize` hands back the body alone; the chrome goes to its own documents so a header edited
    // from ANY page is a store-wide edit (founder call 2026-08-16). Adopt first, so the in-memory chrome
    // every other page composes from reflects the edit even if the PUT is still in flight.
    const chrome = serializeChrome(s);
    if (chrome) adoptEditedChrome(chrome.header, chrome.footer);
    const pageDocument = currentDocument();
    // IDENTITY GUARD. The document carries the page it belongs to (`meta` → template/handle/locale); the
    // route says where we are about to write it. They must be the same page. When they are not, something
    // moved the route out from under an unsaved document — and writing anyway means the seller's edit lands
    // on a template she never opened, silently overwriting it. That happened for real: a heading deleted on
    // a new template was found missing from the store's `default` page, which still had never been edited.
    // Refuse the write, keep the document dirty, and surface it — losing a save is recoverable, writing to
    // the wrong page is not.
    if (
      pageDocument.handle !== s.route.handle ||
      pageDocument.template !== s.route.template ||
      pageDocument.locale !== (s.route.locale ?? pageDocument.locale)
    ) {
      inFlight = false;
      store().setState({
        saveState: 'error',
        dirty: true,
        // NOTHING went over the wire here, so there is no status and no server message — without this
        // the seller (and the console) get a bare "Couldn't save" for a refusal only the editor knows about.
        saveFailure: {
          kind: 'route-mismatch',
          detail: `${pageDocument.template}/${pageDocument.handle}`,
          reason,
        },
      });
      console.error(
        `[autosave] refused: document is ${pageDocument.template}/${pageDocument.handle}/${pageDocument.locale}` +
          ` but the route is ${s.route.template}/${s.route.handle}/${s.route.locale ?? '-'}`,
      );
      return;
    }
    const res = await builderApi.saveDraft(s.route, pageDocument, s.serverVersion ?? 0);
    // Only the chrome documents that actually changed are written (see saveChromeDrafts). A failure here
    // throws into the same handler as a page save — a 409 on the header must park the editor in
    // 'conflict' exactly like a 409 on the body, never fail silently.
    await saveChromeDrafts(pageDocument);
    store().setState((st) => {
      st.serverVersion = res.version; // swap the optimistic-lock token; NO re-fetch
      st.hasUnpublishedChanges = true;
      st.lastSavedAt = Date.now();
      // Still dirty only if the seller edited WHILE this PUT was in flight.
      const stillDirty = serializedDoc() !== marker;
      st.dirty = stillDirty;
      st.saveState = stillDirty ? 'unsaved' : 'saved';
      st.saveFailure = null;
    });
    backoffMs = BACKOFF_MIN_MS; // a success resets the offline backoff ladder
    clearBackoff();
    inFlight = false;
    // A queued follow-up (or a mid-flight edit) → schedule one more save.
    if (pendingFollowUp || store().getState().dirty) {
      pendingFollowUp = false;
      scheduleAutosave();
    }
  } catch (e) {
    inFlight = false;
    handleSaveError(e, reason);
  }
}

/** Public alias for the topbar "Save draft" / Cmd+S path. */
export function saveDraftNow(): Promise<void> {
  return flushSave('manual');
}

function handleSaveError(e: unknown, reason: SaveReason): void {
  // Every branch below records WHY, so the topbar's Retry can name it instead of repeating a doomed
  // attempt in silence (founder, 2026-09-18).
  const fail = (kind: SaveFailure['kind'], detail?: string): void => {
    store().setState({
      saveState: kind === 'network' ? 'offline' : 'error',
      dirty: true,
      saveFailure: {
        kind,
        status: e instanceof HttpError ? e.status : undefined,
        detail: detail ?? (e instanceof Error ? e.message : undefined),
        reason,
      },
    });
  };

  if (e instanceof ConflictError) {
    // 409 stale @Version — park autosave; the reconcile UI drives the next write (§5.5). Keep dirty.
    cancelAutosave();
    store().setState({ saveState: 'conflict', dirty: true, saveFailure: null });
    return;
  }
  if (e instanceof NoStoreError) {
    // The store vanished under us — surface as an error; the shell handles the no-store redirect on load.
    fail('no-store');
    return;
  }
  if (e instanceof SessionExpiredError) {
    // Refresh failed two layers down; the load/boot path bounces to login. Keep the doc + dirty.
    fail('session');
    return;
  }
  if (isStoreChangedError(e)) {
    // The ACCOUNT switched store while this tab was open (founder 2026-09-17). PARK, like a conflict:
    // this document's next token names the OTHER shop, so every retry — the backoff, the next edit, the
    // tab-hide keepalive — would be another attempt to write this page into a store it does not belong
    // to. The gate over the whole editor has already taken the screen; nothing here needs to speak.
    cancelAutosave();
    fail('store-changed');
    return;
  }
  if (isNetworkError(e)) {
    // Offline / transport failure → keep dirty, back off, resume on reconnect (§5.5). Don't lose the doc.
    fail('network');
    queueBackoffRetry();
    return;
  }
  // Unknown / 5xx / 404 → generic error, keep dirty so a retry (manual or next edit) can recover.
  fail('server');
}

/** Exponential backoff retry while offline; also resumes immediately on the `online` event. */
function queueBackoffRetry(): void {
  clearBackoff();
  backoffTimer = setTimeout(() => {
    backoffTimer = null;
    // Only retry if still dirty and not already recovered/conflicted.
    const st = store().getState();
    if (st.dirty && st.saveState === 'offline') {
      void flushSave('auto');
    }
    // Climb the ladder for the next failure.
    backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
  }, backoffMs);
}

// ── publish (build-plan §5.7) ─────────────────────────────────────────────────

/**
 * Publish the current draft to the live storefront (build-plan §5.7). Flush any pending draft first
 * (so the published snapshot is the seller's latest edit), then POST /publish. A conflict during the
 * pre-flush parks in 'conflict' and we DON'T publish over it. On success the draft and published
 * snapshot are in lockstep → no unpublished changes.
 */
export async function publish(): Promise<boolean> {
  if (isReadOnlyView()) return false; // superadmin consultation: nothing may be written
  // A PREVIOUS publish that failed left `saveState: 'error'` behind — and the guard below ("draft save
  // failed; don't publish stale") would then veto every retry of that publish without sending anything.
  // Only a failure of THIS attempt's pre-flush may stop it, so the stale publish error is dropped first.
  if (store().getState().saveFailure?.reason === 'publish') {
    store().setState((st) => {
      st.saveState = st.dirty ? 'unsaved' : 'saved';
      st.saveFailure = null;
    });
  }
  // 1. Make sure the draft is saved first (carries the latest edit into the publish).
  if (store().getState().dirty) await flushSave('publish');
  const s = store().getState();
  if (s.saveState === 'conflict') return false; // don't publish over a conflict
  if (s.saveState === 'offline' || s.saveState === 'error') return false; // draft save failed; don't publish stale

  // THEME WORKSPACE (spec 0032 — one draft per theme): publishing from inside a workspace publishes
  // the THEME (copy every workspace draft over the live handle + release the staged settings), not
  // the workspace row itself — workspace handles must never be published as such.
  if (isThemeWorkspaceHandle(s.route.handle)) return store().getState().publishTheme();

  store().setState({ saveState: 'saving' });
  try {
    await builderApi.publish(s.route);
    // Publishing a page publishes the store's chrome with it: the visitor landing on this page sees
    // both, and a freshly published body wrapped in a stale header is the mismatch this model removes.
    // No-op when the chrome has nothing unpublished.
    await publishChromeDrafts();
    store().setState((st) => {
      st.publishedAt = new Date().toISOString();
      st.hasUnpublishedChanges = false;
      st.saveState = st.dirty ? 'unsaved' : 'saved';
      st.saveFailure = null;
    });
    return true;
  } catch (e) {
    handleSaveError(e, 'publish');
    return false;
  }
}

// ── 409 conflict reconcile (build-plan §5.5) ──────────────────────────────────

/**
 * "Keep my changes" — deliberate last-writer-wins by seller choice (§5.5 / D7). Re-GET to learn the
 * latest `@Version`, then re-PUT the IN-MEMORY document with that version. We never auto-merge a
 * section tree; the seller explicitly chose to overwrite the other write with their own.
 */
export async function reconcileKeepMine(): Promise<boolean> {
  const s = store().getState();
  try {
    const latest = await builderApi.getDraft(s.route);
    // Same deliberate last-writer-wins for the chrome rows: adopt their fresh `@Version` and force a
    // re-PUT of the header/footer the seller has in front of them. Without this, "keep my changes" would
    // re-save the body and leave a 409'd header edit stuck forever.
    await reconcileChromeVersions();
    store().setState((st) => {
      st.serverVersion = latest.version; // adopt the fresh version so the re-PUT is accepted
      st.publishedAt = latest.publishedAt;
      st.hasUnpublishedChanges = latest.hasUnpublishedChanges;
      st.dirty = true;
      st.saveState = 'unsaved';
    });
    await flushSave('manual');
    return store().getState().saveState !== 'conflict';
  } catch (e) {
    handleSaveError(e, 'manual');
    return false;
  }
}

/**
 * "Discard & reload" — drop local unsaved edits, re-GET the server document, replace the store, and
 * reset history (§5.5). Non-destructive in the sense that the seller explicitly chose to discard.
 */
export async function reconcileDiscardAndReload(): Promise<boolean> {
  cancelAutosave();
  try {
    await store().getState().load(store().getState().route);
    return true;
  } catch {
    return false;
  }
}

// ── keepalive last-save (build-plan §5.4 robust last-save) ─────────────────────

/**
 * The data-loss guard for a hard tab-close while a debounced edit is still pending and the doc is
 * dirty (build-plan §5.4 / DoD #7). Uses `fetch(..., { keepalive: true })` — which, UNLIKE
 * `navigator.sendBeacon`, carries request headers, so the PUT includes `Authorization: Bearer <token>`
 * and survives the page going away. This is NOT a native popup; there is no UI involved.
 *
 * Fired on `visibilitychange → hidden` and on `blur` — which is to say every time the seller clicks
 * another tab, another window, the address bar or the devtools, not only when she closes the editor.
 *
 * THE PHANTOM CONFLICT IT USED TO CAUSE (founder, 2026-09-19: "the builder keeps showing me this, but I
 * am the only one working"). This PUT is a real write: the row moves to a new `@Version` whether or not
 * anyone reads the answer. The answer was dropped on the floor — so the editor kept the OLD token, and
 * the very next autosave was a stale write the backend answered 409 to. "This page changed elsewhere"
 * with nobody else anywhere near it: the editor was conflicting with its own last request, once per
 * blur, forever. The fix is the whole point of the code below — the page is still ALIVE on a blur or a
 * tab switch, so the response DOES arrive, and we put it back into the store:
 *
 *  - the version swaps exactly like an ordinary save (no re-fetch), page row and chrome rows alike;
 *  - `dirty` clears only if nothing was typed while the PUT was on the wire (marker comparison);
 *  - the single-flight gate is HELD until the response lands, so an edit made one second later cannot
 *    fire an autosave still carrying the token this PUT has just superseded;
 *  - a response that names a page she has since LEFT is dropped — that version belongs to the other row.
 *
 * On a real unload none of that runs (the document is gone), which is exactly the old behaviour: the
 * PUT still ships, and the next open re-reads the version from the server anyway.
 *
 * Returns true if a request was actually dispatched (used by tests).
 */
export function keepaliveFlush(): boolean {
  clearDebounceTimers();
  if (isReadOnlyView()) return false; // superadmin consultation: nothing may be written
  const s = store().getState();
  if (!s.dirty || s.saveState === 'conflict') return false;
  // SINGLE-FLIGHT, read from the module flag and NOT from `saveState` (which every commit() resets to
  // 'unsaved'): a PUT already carries this document, and a second one would only race it on the same
  // `@Version` — the older shape of this guard read `saveState` and let exactly that through.
  if (inFlight) return false;

  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const qs = routeParams(s.route).toString();

  try {
    const pageDocument = currentDocument();
    // What is going over the wire, and where it belongs — both read back when the response lands.
    const marker = serializedDoc();
    const route = s.route;

    // Hold the gate from HERE, before the first fetch: releaseKeepalive() puts it back.
    inFlight = true;
    keepaliveHolding = true;
    keepaliveTimer = setTimeout(releaseKeepalive, KEEPALIVE_RESPONSE_MS);

    const page = fetch(`${deriveApiBaseUrl()}/api/builder/page?${qs}`, {
      method: 'PUT',
      keepalive: true,
      credentials: 'include',
      headers,
      body: JSON.stringify({ document: pageDocument, version: s.serverVersion ?? 0 }),
    });
    // The chrome is a separate row, so it needs its own keepalive PUT — otherwise closing the tab right
    // after a header edit would save the body and silently lose the header.
    const chrome = serializeChrome(s);
    if (chrome) adoptEditedChrome(chrome.header, chrome.footer);
    const chromePuts = keepaliveChromePayloads(pageDocument).map((payload) => {
      const chromeQs = routeParams(payload.route).toString();
      return fetch(`${deriveApiBaseUrl()}/api/builder/page?${chromeQs}`, {
        method: 'PUT',
        keepalive: true,
        credentials: 'include',
        headers,
        body: JSON.stringify({ document: payload.document, version: payload.version }),
      })
        .then(async (res) => {
          const version = await versionOf(res);
          if (version === null) return;
          adoptChromeSavedVersion(payload.key, payload.template, version, payload.marker);
        })
        .catch(() => undefined);
    });

    void page
      .then(async (res) => {
        const version = await versionOf(res);
        // A 409 / 5xx here says nothing new: the document stays dirty and the next ordinary save is the
        // one that gets to classify the failure and show it. Silence is the right answer.
        if (version === null) return;
        adoptKeepaliveVersion(route, version, marker);
      })
      .catch(() => undefined)
      .finally(() => {
        // The gate is only released once BOTH halves have landed: the chrome rows carry their own
        // tokens, and an autosave starting on a stale one of those 409s just the same.
        void Promise.allSettled(chromePuts).then(releaseKeepalive);
      });
    return true;
  } catch {
    // A keepalive fetch should not throw synchronously; if it does, there's nothing more we can do —
    // but the gate must not stay shut on the strength of a request that never left.
    releaseKeepalive();
    return false;
  }
}

/** The `{version}` a save response carries, or null for a non-2xx / unreadable / non-JSON answer. */
async function versionOf(res: Response): Promise<number | null> {
  if (!res.ok) return null;
  try {
    const text = await res.text();
    if (!text) return null;
    const body: unknown = JSON.parse(text);
    const version = (body as { version?: unknown }).version;
    return typeof version === 'number' ? version : null;
  } catch {
    return null;
  }
}

/**
 * Put a landed keepalive version back into the store — the page half of the phantom-conflict fix.
 * Refuses when the editor has moved on: a conflict is already being reconciled (that flow owns the next
 * write), or the seller opened ANOTHER page, whose `serverVersion` this token does not name. Writing it
 * there would hand the new page a foreign token and 409 the first real edit she makes on it.
 */
function adoptKeepaliveVersion(route: BuilderRoute, version: number, marker: string): void {
  const st = store().getState();
  if (st.saveState === 'conflict') return;
  if (
    st.route.template !== route.template ||
    st.route.handle !== route.handle ||
    (st.route.locale ?? null) !== (route.locale ?? null)
  ) {
    return;
  }
  store().setState((d) => {
    d.serverVersion = version; // swap the optimistic-lock token; NO re-fetch
    d.hasUnpublishedChanges = true;
    d.lastSavedAt = Date.now();
    // Dirty only if she typed while the PUT was on the wire — that keystroke is not on the server.
    const stillDirty = serializedDoc() !== marker;
    d.dirty = stillDirty;
    d.saveState = stillDirty ? 'unsaved' : 'saved';
    d.saveFailure = null;
  });
}

/**
 * Give the single-flight gate back after a keepalive PUT, and let a queued edit through. Idempotent:
 * the response and the {@link KEEPALIVE_RESPONSE_MS} floor race each other, and whichever loses must
 * not clear `inFlight` out from under an ordinary save that has started in the meantime.
 */
function releaseKeepalive(): void {
  if (keepaliveTimer) {
    clearTimeout(keepaliveTimer);
    keepaliveTimer = null;
  }
  if (!keepaliveHolding) return;
  keepaliveHolding = false;
  inFlight = false;
  if (pendingFollowUp || store().getState().dirty) {
    pendingFollowUp = false;
    scheduleAutosave();
  }
}

// ── force-flush trigger wiring (window events) — installed once by the shell ───

let triggersInstalled = false;

/**
 * Install the force-flush window listeners (build-plan §5.4 / §7.9). Idempotent — StrictMode mounts
 * the shell twice in dev, so a second call is a no-op. Returns a teardown.
 *  - `visibilitychange → hidden` + `blur` → keepalive last-save (carries the Bearer).
 *  - `online` → resume autosave immediately (cancel the backoff wait).
 *  - `beforeunload` → ONLY a native browser data-loss guard (returnValue) as a last resort; the real
 *    save is the keepalive flush above. This is the one accepted native exception (build-plan §7.9).
 */
export function installAutosaveTriggers(): () => void {
  if (triggersInstalled) return () => undefined;
  triggersInstalled = true;

  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') keepaliveFlush();
  };
  const onBlur = (): void => {
    keepaliveFlush();
  };
  const onOnline = (): void => {
    clearBackoff();
    backoffMs = BACKOFF_MIN_MS;
    const st = store().getState();
    if (st.dirty && (st.saveState === 'offline' || st.saveState === 'error')) {
      void flushSave('auto');
    }
  };
  const onBeforeUnload = (e: BeforeUnloadEvent): void => {
    const st = store().getState();
    if (st.dirty && st.saveState !== 'saved') {
      e.preventDefault();
      // Legacy browsers need returnValue set; the keepalive flush is the actual save.
      e.returnValue = '';
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('blur', onBlur);
  window.addEventListener('online', onOnline);
  window.addEventListener('beforeunload', onBeforeUnload);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('beforeunload', onBeforeUnload);
    triggersInstalled = false;
  };
}

// ── internals ─────────────────────────────────────────────────────────────────

export type SaveReason = 'auto' | 'manual' | 'publish' | 'route-leave';

function currentDocument() {
  return serialize(store().getState());
}

/**
 * The mid-flight change marker. Deliberately the COMPOSED document (body + chrome), not the saved page
 * shape: a header edit made while a PUT is in flight changes no byte of the page document, so a
 * body-only marker would report "unchanged" and drop that edit on the floor.
 */
function serializedDoc(): string {
  return JSON.stringify(serializeComposed(store().getState()));
}

/** Route → query params, omitting `locale` when absent so the backend resolves the STORE's own
 *  language (feature 0050 lot 0). An empty `locale=` would read as an invalid locale. */
function routeParams(route: {
  template: string;
  handle: string;
  locale?: string;
}): URLSearchParams {
  const qs = new URLSearchParams({ template: route.template, handle: route.handle });
  if (route.locale) qs.set('locale', route.locale);
  return qs;
}
